import * as React from "react";
import { format, parse, isValid } from "date-fns";
import { CalendarIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

// Parse "YYYY-MM-DD" → Date (no timezone shift)
function parseISODate(s) {
  if (!s) return undefined;
  const d = parse(s, "yyyy-MM-dd", new Date());
  return isValid(d) ? d : undefined;
}

// Date → "YYYY-MM-DD"
function toISODate(d) {
  if (!d || !isValid(d)) return "";
  return format(d, "yyyy-MM-dd");
}

/**
 * Drop-in replacement for `<input type="date">`. Renders a button showing the
 * formatted date and opens a shadcn Calendar popover for selection.
 *
 *   <DatePicker value="2026-06-29" onChange={(s) => setDate(s)} />
 *
 * `value` and the value passed to `onChange` are both ISO "YYYY-MM-DD" strings.
 */
export function DatePicker({
  value,
  onChange,
  className,
  placeholder = "Select date",
  disabled,
  testId,
  align = "start",
  size = "md", // "sm" | "md"
}) {
  const [open, setOpen] = React.useState(false);
  const selected = parseISODate(value);

  const handleSelect = (d) => {
    if (!d) return;
    onChange(toISODate(d));
    setOpen(false);
  };

  const sizeClasses = size === "sm"
    ? "h-7 px-1.5 text-xs gap-1"
    : "h-9 px-3 text-sm gap-2";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "inline-flex items-center rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 focus:border-[#145c8c] focus:outline-none disabled:opacity-50",
            sizeClasses,
            !selected && "text-slate-400",
            className,
          )}
        >
          <CalendarIcon className={cn("shrink-0 text-slate-400", size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5")} />
          <span className="truncate">
            {selected ? format(selected, "EEE, dd MMM yyyy") : placeholder}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align={align}>
        <Calendar
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
